// Work mode's query options (M2-07): one place for the keys and SDK calls of spaces, boards,
// views, fields, tasks, the inbox and templates, so each screen's calls are visible from what it
// mounts. Every key starts `["company", co, "work"]`: a write invalidates that prefix and every
// Work screen (and the sidebar's tree and the inbox count) refetches once.
//
// While the boards, inbox and templates routes are pending on an API (M2-03, M2-04, M1-06 build
// them; until then the route is not registered and answers 404), `workLiveQuery` says so once,
// without a toast, and Work mode shows that instead of failing call by call.
import { ApiError } from "@companyos/sdk";
import { infiniteQueryOptions, type QueryClient, queryOptions } from "@tanstack/react-query";
import type { Api } from "../../api/client";

export const workKeys = {
  all: (co: string) => ["company", co, "work"] as const,
  live: (co: string) => ["company", co, "work", "live"] as const,
  spaces: (co: string) => ["company", co, "work", "spaces"] as const,
  boards: (co: string) => ["company", co, "work", "boards"] as const,
  summaries: (co: string) => ["company", co, "work", "boards", "summary"] as const,
  sidebar: (co: string, space: string) => ["company", co, "work", "space", space] as const,
  board: (co: string, id: string) => ["company", co, "work", "board", id] as const,
  views: (co: string, board: string) => ["company", co, "work", "board", board, "views"] as const,
  fields: (co: string, board: string) => ["company", co, "work", "board", board, "fields"] as const,
  boardTasks: (co: string, board: string, view: string | null) =>
    ["company", co, "work", "board", board, "tasks", view ?? "default"] as const,
  boardAll: (co: string, board: string) =>
    ["company", co, "work", "board", board, "all-tasks"] as const,
  task: (co: string, id: string) => ["company", co, "work", "task", id] as const,
  subtasks: (co: string, id: string) => ["company", co, "work", "task", id, "subtasks"] as const,
  history: (co: string, id: string) => ["company", co, "work", "task", id, "history"] as const,
  /** AGENT-MENTION: the task's handoffs to its DE seat holders (the drawer's status lines). */
  agentWakes: (co: string, id: string) =>
    ["company", co, "work", "task", id, "agent-wakes"] as const,
  thread: (co: string, id: string) => ["company", co, "work", "thread", id] as const,
  myTasks: (co: string) => ["company", co, "work", "my-tasks"] as const,
  companyTasks: (co: string) => ["company", co, "work", "company-tasks"] as const,
  search: (co: string, q: string) => ["company", co, "work", "search", q] as const,
  inbox: (co: string, filter: string) => ["company", co, "work", "inbox", filter] as const,
  // M7-02: pages
  pages: (co: string) => ["company", co, "work", "pages"] as const,
  page: (co: string, id: string) => ["company", co, "work", "page", id] as const,
  pageVersions: (co: string, id: string) =>
    ["company", co, "work", "page", id, "versions"] as const,
  pageVersion: (co: string, id: string, v: number) =>
    ["company", co, "work", "page", id, "versions", v] as const,
  pageSearch: (co: string, q: string) => ["company", co, "work", "page-search", q] as const,
  // DOCS-KB: every doc, archived ones included (the Docs screen), and a page's agent readers.
  pagesAll: (co: string) => ["company", co, "work", "pages", "all"] as const,
  pageReaders: (co: string, id: string) => ["company", co, "work", "page", id, "readers"] as const,
};

/**
 * DOCS-KB: every page the caller may view, archived ones included, following `next_cursor` (the
 * Docs screen filters and sorts on the client). A route the API does not serve yet reads as none.
 */
export const pagesAllQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: workKeys.pagesAll(co),
    queryFn: async () => {
      type Row = Awaited<ReturnType<Api["client"]["pages"]["list"]>>["data"][number];
      const rows: Row[] = [];
      let cursor: string | undefined;
      do {
        const page = await pendingAsNull(() =>
          api.client.pages.list({
            params: { co },
            query: { limit: WORK_PAGE, include_archived: "true", ...(cursor ? { cursor } : {}) },
          }),
        );
        if (!page) break;
        rows.push(...page.data);
        cursor = page.next_cursor ?? undefined;
      } while (cursor);
      return rows;
    },
    // UPLOAD-PRIVATE-FIRST: an upload's text is read in the background; the list looks again
    // while one is still processing, so its chip clears in place.
    refetchInterval: (q) =>
      q.state.data?.some((r) => r.upload?.status === "processing") ? UPLOAD_POLL_MS : false,
  });

/** UPLOAD-PRIVATE-FIRST: how often a doc still being read is looked at again. */
export const UPLOAD_POLL_MS = 2500;

/** The agents whose knowledge base includes the page ("Used by"); none while the route is pending. */
export const pageReadersQuery = (api: Api, co: string, id: string) =>
  queryOptions({
    queryKey: workKeys.pageReaders(co, id),
    queryFn: async () =>
      (await pendingAsNull(() => api.client.pages.readers({ params: { co, id } })))?.data ?? [],
  });

/**
 * DOCS-ALWAYS-IN-MEMORY: whether the doc is in company memory (the "In memory" / "Syncing" chip);
 * null while the route is pending. Under the page's own key, so a reload of the page refreshes it.
 */
export const pageMemoryQuery = (api: Api, co: string, id: string) =>
  queryOptions({
    queryKey: [...workKeys.page(co, id), "memory"] as const,
    queryFn: () => pendingAsNull(() => api.client.pages.memory({ params: { co, id } })),
    // an upload goes within a minute: look again while it is on its way
    refetchInterval: (q) => (q.state.data?.state === "syncing" ? 30_000 : false),
  });

/**
 * RECALL-KB-LINKS: what company memory links to the doc (the Related tab). Anything but a ready
 * answer (a route pending, memory off, a failed read) reads as null: the tab is not shown.
 */
export const pageRelatedQuery = (api: Api, co: string, id: string) =>
  queryOptions({
    queryKey: [...workKeys.page(co, id), "related"] as const,
    queryFn: async () => {
      const r = await pendingAsNull(() => api.client.pages.related({ params: { co, id } })).catch(
        () => null,
      );
      return r?.state === "ready" ? r : null;
    },
    retry: false,
  });

/** RECALL-KB-LINKS: a space's "About these docs" (Docs home); null when there is nothing to show. */
export const spaceDocsMemoryQuery = (api: Api, co: string, spaceId: string) =>
  queryOptions({
    queryKey: [...workKeys.sidebar(co, spaceId), "docs-memory"] as const,
    queryFn: async () => {
      const r = await pendingAsNull(() =>
        api.client.spaces.docsMemory({ params: { co, id: spaceId } }),
      ).catch(() => null);
      return r?.state === "ready" ? r : null;
    },
    retry: false,
  });

/**
 * Every page the caller may view across the company (the sidebar's tree; M7-02). A route the API
 * does not serve yet reads as no pages.
 */
/**
 * MULTI-SPACE-ACCESS: every page share the caller can see (the sidebar's Also-in rows, the Share
 * dialog). Under `pages`, so every page write's invalidation refreshes it.
 */
/** KB-UPLOAD-2: the people and seats (DEs) a page is shared with directly. */
export const pageMembersQuery = (api: Api, co: string, id: string) =>
  queryOptions({
    queryKey: [...workKeys.pages(co), "members", id] as const,
    queryFn: async () =>
      (await pendingAsNull(() => api.client.pages.members.list({ params: { co, id } }))) ?? {
        data: [],
      },
  });

export const pageSharesQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: [...workKeys.pages(co), "shares"] as const,
    queryFn: async () =>
      (await pendingAsNull(() => api.client.pages.shares.list({ params: { co }, query: {} }))) ?? {
        data: [],
      },
  });

export const pagesQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: workKeys.pages(co),
    queryFn: async () =>
      (await pendingAsNull(() =>
        api.client.pages.list({ params: { co }, query: { limit: WORK_PAGE } }),
      )) ?? { data: [], next_cursor: null, prev_cursor: null },
  });

export const pageQuery = (api: Api, co: string, id: string) =>
  queryOptions({
    queryKey: workKeys.page(co, id),
    queryFn: () => api.client.pages.get({ params: { co, id } }),
    // UPLOAD-PRIVATE-FIRST: the doc fills in place when its text has been read.
    refetchInterval: (q) =>
      q.state.data?.upload?.status === "processing" ? UPLOAD_POLL_MS : false,
  });

export const pageVersionsQuery = (api: Api, co: string, id: string) =>
  queryOptions({
    queryKey: workKeys.pageVersions(co, id),
    queryFn: () => api.client.pages.versions({ params: { co, id }, query: { limit: 100 } }),
  });

export const pageVersionQuery = (api: Api, co: string, id: string, version: number) =>
  queryOptions({
    queryKey: workKeys.pageVersion(co, id, version),
    queryFn: () => api.client.pages.version({ params: { co, id, version } }),
    staleTime: Number.POSITIVE_INFINITY,
  });

export const pageSearchQuery = (api: Api, co: string, q: string) =>
  queryOptions({
    queryKey: workKeys.pageSearch(co, q),
    queryFn: () => api.client.pages.search({ params: { co }, body: { query: q, limit: 20 } }),
    staleTime: 10_000,
  });

/** WIKI-LINKS-GRAPH: the doc's links out (live titles, missing ones) and backlinks. */
export const pageLinksQuery = (api: Api, co: string, id: string) =>
  queryOptions({
    queryKey: [...workKeys.page(co, id), "links"] as const,
    queryFn: () => api.client.pages.links({ params: { co, id }, query: {} }),
    retry: false,
  });

/** WIKI-LINKS-GRAPH: the doc's local graph, 1 or 2 hops. */
export const pageGraphQuery = (api: Api, co: string, id: string, depth: 1 | 2) =>
  queryOptions({
    queryKey: [...workKeys.page(co, id), "graph", depth] as const,
    queryFn: () =>
      api.client.pages.graph({ params: { co, id }, query: { depth: depth === 2 ? "2" : "1" } }),
    retry: false,
  });

export type DocsGraphFilter = {
  space_id?: string | undefined;
  tag?: string | undefined;
  orphans?: "show" | "hide" | "only" | undefined;
};

/** WIKI-LINKS-GRAPH: the docs' graph (a space's or the company's), capped by the API. */
export const docsGraphQuery = (api: Api, co: string, f: DocsGraphFilter) =>
  queryOptions({
    queryKey: [
      ...workKeys.pages(co),
      "graph",
      f.space_id ?? "",
      f.tag ?? "",
      f.orphans ?? "",
    ] as const,
    queryFn: () =>
      api.client.pages.graphAll({
        params: { co },
        query: {
          ...(f.space_id ? { space_id: f.space_id } : {}),
          ...(f.tag ? { tag: f.tag } : {}),
          ...(f.orphans && f.orphans !== "show" ? { orphans: f.orphans } : {}),
        },
      }),
    retry: false,
  });

/** Every Work list reads at most this many rows a page (lists show "Load more" after it). */
export const WORK_PAGE = 200;

/** A route that is not registered on this API yet (the server's 404 for an unknown path). */
export function isPendingRoute(error: unknown): boolean {
  return error instanceof ApiError && error.status === 404;
}

/** A read whose route may not be registered yet: `null` instead of an error (and no toast). */
export async function pendingAsNull<T>(read: () => Promise<T>): Promise<T | null> {
  try {
    return await read();
  } catch (e) {
    if (isPendingRoute(e)) return null;
    throw e;
  }
}

/**
 * Whether this API serves Work (`spaces.list` answers). A 404 on the company's collection route
 * can only mean the route is not registered (the company itself is visible), so it is a fact,
 * not an error: no retry, no toast.
 */
export const workLiveQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: workKeys.live(co),
    queryFn: async () => {
      try {
        await api.client.spaces.list({ params: { co }, query: { limit: 1 } });
        return true;
      } catch (e) {
        if (isPendingRoute(e)) return false;
        throw e;
      }
    },
    staleTime: 5 * 60_000,
    refetchInterval: false,
    retry: false,
  });

/**
 * Spaces and boards refetch on every mount: the persisted cache is written at most once a second,
 * so a reload right after a move/colour/lead write restores a stale copy that staleTime would trust.
 */
export const spacesQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: workKeys.spaces(co),
    refetchOnMount: "always",
    queryFn: () => api.client.spaces.list({ params: { co }, query: { limit: WORK_PAGE } }),
  });

export const boardsQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: workKeys.boards(co),
    refetchOnMount: "always",
    queryFn: () => api.client.boards.list({ params: { co }, query: { limit: WORK_PAGE } }),
  });

/**
 * W1-FIXES (T3 review): every live board the caller sees, every page followed (`boardsQuery`
 * reads the first page only), for pickers that must offer them all.
 */
export const boardsAllQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: [...workKeys.boards(co), "all"],
    queryFn: async () => {
      type Row = Awaited<ReturnType<Api["client"]["boards"]["list"]>>["data"][number];
      const rows: Row[] = [];
      let cursor: string | undefined;
      do {
        const page = await api.client.boards.list({
          params: { co },
          query: { limit: WORK_PAGE, ...(cursor ? { cursor } : {}) },
        });
        rows.push(...page.data);
        cursor = page.next_cursor ?? undefined;
      } while (cursor);
      return rows;
    },
  });

export type BoardSummary = Awaited<ReturnType<Api["client"]["boards"]["summary"]>>["data"][number];

/**
 * WORK-OS: every board with its counts (the overview, the sidebar's open counts, the board
 * header's progress). An API without the route yet reads as no summaries.
 */
export const boardSummariesQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: workKeys.summaries(co),
    queryFn: async () =>
      (await pendingAsNull(() => api.client.boards.summary({ params: { co } })))?.data ?? [],
  });

export const spaceSidebarQuery = (api: Api, co: string, id: string) =>
  queryOptions({
    queryKey: workKeys.sidebar(co, id),
    queryFn: () => api.client.spaces.sidebar({ params: { co, id } }),
  });

export const boardQuery = (api: Api, co: string, id: string) =>
  queryOptions({
    queryKey: workKeys.board(co, id),
    queryFn: () => api.client.boards.get({ params: { co, id } }),
  });

export const viewsQuery = (api: Api, co: string, board: string) =>
  queryOptions({
    queryKey: workKeys.views(co, board),
    queryFn: () => api.client.views.list({ params: { co, id: board } }),
  });

export const fieldsQuery = (api: Api, co: string, board: string) =>
  queryOptions({
    queryKey: workKeys.fields(co, board),
    queryFn: () => api.client.fields.list({ params: { co, id: board } }),
  });

/** A saved view, evaluated by the server (`groups[]` when grouped). */
export const boardTasksQuery = (api: Api, co: string, board: string, view: string | null) =>
  queryOptions({
    queryKey: workKeys.boardTasks(co, board, view),
    queryFn: () =>
      api.client.boards.tasks({
        params: { co, id: board },
        query: { limit: WORK_PAGE, ...(view ? { view } : {}) },
      }),
  });

/** Every task of a board (unsaved view changes are evaluated over these). */
export const boardAllTasksQuery = (api: Api, co: string, board: string) =>
  infiniteQueryOptions({
    queryKey: workKeys.boardAll(co, board),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      api.client.tasks.list({
        params: { co },
        query: { board_id: board, limit: WORK_PAGE, ...(pageParam ? { cursor: pageParam } : {}) },
      }),
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  });

/** The company's tasks across boards (Work home's "Blocked or overdue"). */
export const companyTasksQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: workKeys.companyTasks(co),
    queryFn: () => api.client.tasks.list({ params: { co }, query: { limit: WORK_PAGE } }),
  });

export const taskQuery = (api: Api, co: string, id: string) =>
  queryOptions({
    queryKey: workKeys.task(co, id),
    queryFn: () => api.client.tasks.get({ params: { co, id } }),
  });

export const subtasksQuery = (api: Api, co: string, id: string) =>
  queryOptions({
    queryKey: workKeys.subtasks(co, id),
    queryFn: () =>
      api.client.tasks.list({ params: { co }, query: { parent_task_id: id, limit: WORK_PAGE } }),
  });

export const historyQuery = (api: Api, co: string, id: string) =>
  queryOptions({
    queryKey: workKeys.history(co, id),
    queryFn: () => api.client.tasks.history({ params: { co, id }, query: { limit: 50 } }),
  });

/** The task's thread (created on first use) and its newest messages. */
export const threadQuery = (api: Api, co: string, taskId: string) =>
  queryOptions({
    queryKey: workKeys.thread(co, taskId),
    queryFn: async () => {
      const { thread_id } = await api.client.tasks.thread({ params: { co, id: taskId } });
      const page = await api.client.messages.list({
        params: { co, id: thread_id },
        query: { limit: 100 },
      });
      return { thread_id, messages: page.data };
    },
  });

export const myTasksQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: workKeys.myTasks(co),
    queryFn: () => api.client.my.tasks({ params: { co }, query: { limit: WORK_PAGE } }),
  });

export const taskSearchQuery = (api: Api, co: string, q: string) =>
  queryOptions({
    queryKey: workKeys.search(co, q),
    queryFn: () => api.client.tasks.search({ params: { co }, body: { query: q, limit: 20 } }),
    staleTime: 10_000,
  });

export type InboxFilter =
  | "all"
  // INBOX-PREFS: Waiting on me (approvals, a DE's question) and DMs.
  | "waiting"
  | "dms"
  | "unread"
  | "mentions"
  | "approvals"
  | "followed"
  | "memory"
  | "snoozed"
  | "done";

export const inboxQuery = (api: Api, co: string, filter: InboxFilter) =>
  queryOptions({
    queryKey: workKeys.inbox(co, filter),
    // `null`: the inbox routes are not registered on this API yet (M2-04), which is not an error.
    queryFn: () =>
      pendingAsNull(() =>
        api.client.inbox.list({
          params: { co },
          query: { limit: 100, ...(filter === "all" ? {} : { filter }) },
        }),
      ),
  });

/**
 * WORK-PERF: warms a board before it is opened (a sidebar row under the pointer): the board, its
 * views and fields, then the tasks of the view the board opens on (the default one), under the
 * keys the board screen reads. Fresh data is not fetched again (the queries' staleTime).
 */
export function prefetchBoard(queryClient: QueryClient, api: Api, co: string, boardId: string) {
  void queryClient.prefetchQuery(boardQuery(api, co, boardId));
  void queryClient.prefetchQuery(fieldsQuery(api, co, boardId));
  void queryClient
    .fetchQuery({ ...viewsQuery(api, co, boardId), staleTime: 30_000 })
    .then((views) => {
      const list = [...views.data].sort((x, y) => x.position - y.position);
      const view = list.find((v) => v.is_default) ?? list[0] ?? null;
      return queryClient.prefetchQuery(boardTasksQuery(api, co, boardId, view?.id ?? null));
    })
    .catch(() => {});
}

/** After any Work write: every Work list, the tree and the inbox refetch. */
export function invalidateWork(queryClient: QueryClient, co: string) {
  return queryClient.invalidateQueries({
    queryKey: workKeys.all(co),
    predicate: (q) => q.queryKey[3] !== "live",
  });
}
